import { useEffect } from 'react';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Keyboard behavior `ModalLayout` does not provide: Esc closes, focus moves into the dialog (to
 * its `[data-autofocus]` element) when it opens, Tab stays inside it, and focus returns to what
 * had it before.
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose Called on Esc.
 * @param {string} titleId `titleId` given to the `ModalLayout`, to find its dialog.
 */
export const useDialogKeyboard = (isOpen, onClose, titleId) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    const dialog = () => document.querySelector(`[role="dialog"][aria-labelledby="${titleId}"]`);
    dialog()?.querySelector('[data-autofocus]')?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...(dialog()?.querySelectorAll(FOCUSABLE) ?? [])];
      if (focusable.length === 0) {
        // Nothing to focus (e.g. every control is disabled while busy): Tab must not leave the dialog.
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!dialog()?.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [isOpen, onClose, titleId]);
};
